/**
 * 호 (4-digit) 상세 페이지 — CielHS 이미지 169 1:1 mirror.
 *
 * URL: /?audit=onto-detail&hs=4202
 *
 * Layout (skeleton — sub-components 후속 task 에서 채움):
 *   ┌─ 상단 bar (back + breadcrumb + 한↔영 + 인쇄) ──────────┐
 *   ├─ 좌측 호 nav 200px ──┬─ 메인 grid (호·소호·세번 indent) ┤
 *   ├──────────────────────┴──────────────────────────────────┤
 *   └─ 하단 자료 탭 (통칙/부주/류주/호해설/분류사례/이미지) ──┘
 *
 * 데이터: /api/onto/heading-detail?hs=NNNN&lang=kor
 */
import React, { useState, useEffect, useLayoutEffect } from "react";
import {
  ArrowLeft,
  Printer,
  Languages,
  ArrowDown,
  ArrowUp,
  ChevronLeft as ChevLeft,
  ChevronRight as ChevRight,
} from "lucide-react";
import AuditSideNav from "./AuditSideNav";
import HeadingBreadcrumb from "./HeadingBreadcrumb";
import HeadingTabs from "./HeadingTabs";
import EvidencePanel from "./EvidencePanel";
import LeftAiPanel from "./LeftAiPanel";
import { useAuditSearch } from "./AuditSearchContext";
import CenterSearchBar from "./CenterSearchBar";
import { shouldShowFloatingSearch } from "./searchBarPlacement";
import GoogleImageOverlay from "./GoogleImageOverlay";
import HierarchyDrillView from "./HierarchyDrillView";
import SearchResultTabs from "./SearchResultTabs";
import { isLoginHSProduct } from "../config/product";
import WatchStar from "./WatchStar";
import { sectionIdOfChapter } from "./hsHierarchyData";
import "./theme.css";

function useViewportWidth() {
  const [w, setW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return w;
}

export const DEFAULT_EVIDENCE_OPEN = false;

export function detailGridColumns({
  isMobile,
  searchInSidebar,
  leftOpen,
  leftWidth,
  rightOpen,
  evidenceExpanded,
}) {
  if (isMobile) return "1fr";
  const rightWidth = evidenceExpanded ? "520px" : "340px";
  if (searchInSidebar) return rightOpen ? `1fr ${rightWidth}` : "1fr";
  const leftColumn = leftOpen ? `${leftWidth}px` : "32px";
  return rightOpen
    ? `${leftColumn} 1fr ${rightWidth}`
    : `${leftColumn} 1fr`;
}

export function headingTabsScrollTop({
  scrollTop,
  scrollerTop,
  targetTop,
  offset = 16,
}) {
  return Math.max(0, scrollTop + targetTop - scrollerTop - offset);
}

export function shouldJumpBackToTop({
  scrollTop,
  targetScrollTop,
  clientHeight,
  scrollHeight,
  tolerance = 12,
}) {
  const hasScrolled = scrollTop > tolerance;
  const reachedTabs = hasScrolled && scrollTop >= targetScrollTop - tolerance;
  const reachedBottom =
    hasScrolled &&
    scrollHeight > clientHeight + tolerance &&
    scrollTop + clientHeight >= scrollHeight - tolerance;
  return reachedTabs || reachedBottom;
}

function useDetailParams() {
  // URL ?section=N&chapter=NN&hs=NNNN&hsk=NNNNNNNNNN&sq=<검색어> parse — 마운트/URL 변경 시 갱신.
  const [params, setParams] = useState(() => {
    if (typeof window === "undefined")
      return {
        section: null,
        chapter: null,
        hs: null,
        hsk: null,
        fromChapter: null,
        sq: null,
        sub: null,
        tab: null,
      };
    const p = new URLSearchParams(window.location.search);
    return {
      section: p.get("section"),
      chapter: p.get("chapter"),
      hs: p.get("hs"),
      hsk: p.get("hsk"),
      fromChapter: p.get("fromChapter"),
      sq: p.get("sq"),
      sub: p.get("sub"),
      tab: p.get("tab"),
    };
  });
  useEffect(() => {
    const handler = () => {
      const p = new URLSearchParams(window.location.search);
      // 64차: keep-alive 상태에서 다른 화면(통관정보·도구…)의 URL 은 읽지 않는다 — 보던 세번(hsk)·탭을 지우지 않기 위해.
      const audit = p.get("audit");
      if (audit !== "onto-detail" && audit !== "quickref") return;
      setParams({
        section: p.get("section"),
        chapter: p.get("chapter"),
        hs: p.get("hs"),
        hsk: p.get("hsk"),
        fromChapter: p.get("fromChapter"),
        sq: p.get("sq"),
        sub: p.get("sub"),
        tab: p.get("tab"),
      });
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);
  return params;
}

export default function HeadingDetailPage({
  embedded = false,
  hsOverride = null, // master-detail 셸에서 선택된 호(4자리)를 prop 으로 주입 — URL 의존 분리
  compact = false, // 좌측에 외부 네비게이터가 있을 때 — 자체 AI 패널 기본 접힘(폭 확보)
  onHsSelect = null, // 셸에서 호 목록 클릭 시 URL 이동 대신 셸 상태 갱신 콜백 (hs4, name)
}) {
  const params = useDetailParams();
  // hsOverride 가 있으면 그 호로 강제(다른 URL 파라미터 무시) — 우측 패널 단독 구동
  const section = hsOverride ? null : params.section;
  // ?hs= 관용 파싱 — 4/6/10자리(dash·dot 포함) 모두 허용:
  //   항상 호(4자리)로 정규화하고, 10자리 이상이면 세번(hsk)으로도 승격.
  //   (기존엔 정확히 4자리만 매칭돼 ?hs=851590(6)·10자리 딥링크가 부 목록으로 떨어졌음)
  const rawHs = hsOverride || params.hs;
  const hsDigits = rawHs ? String(rawHs).replace(/\D/g, "") : "";
  const hs = hsDigits ? hsDigits.slice(0, 4) : null;
  const chapter = hsOverride ? (hs ? hs.slice(0, 2) : null) : params.chapter;
  // hsk: 명시 ?hsk= 우선, 없으면 ?hs= 가 10자리 이상일 때 앞 10자리를 세번으로.
  // hsOverride(Master-Detail) 모드에선 현재 호에 속한 hsk 만 유효(stale 무시).
  const rawHsk = params.hsk || (hsDigits.length >= 10 ? hsDigits : null);
  const hskDigits = rawHsk
    ? String(rawHsk).replace(/\D/g, "").slice(0, 10)
    : null;
  const hsk = hsOverride
    ? hskDigits && hskDigits.slice(0, 4) === String(hsOverride).slice(0, 4)
      ? hskDigits
      : null
    : hskDigits;
  const fromChapter = params.fromChapter;
  // 검색결과 종합 스테이지 (sq) — 셸(hsOverride) 모드에선 비활성
  const sq = hsOverride ? null : params.sq;
  // 소호 필터 prefix (예 20094 → 2009.4 만) — 현재 호와 일치할 때만 유효(stale 무시)
  const sub =
    params.sub && hs && String(params.sub).startsWith(hs) ? params.sub : null;
  const viewportW = useViewportWidth();
  // 자동 default — compact(셸 내장)이면 좌패널 접힘, 아니면 1280 이상에서 펼침
  const [leftOpen, setLeftOpen] = useState(() => !compact && viewportW >= 1280);
  const [rightOpen, setRightOpen] = useState(DEFAULT_EVIDENCE_OPEN);
  // 좌측 AI 패널 너비 — 사용자가 세로선 드래그로 조절 (다중검색 그리드 등 폭 확보용). localStorage 영속.
  const [leftWidth, setLeftWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem("auditLeftWidth") || "", 10);
    return saved >= 240 && saved <= 720 ? saved : 320;
  });
  useEffect(() => {
    localStorage.setItem("auditLeftWidth", String(leftWidth));
  }, [leftWidth]);
  const handleLeftResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = leftWidth;
    const onMove = (ev) => {
      setLeftWidth(
        Math.min(720, Math.max(240, startW + (ev.clientX - startX))),
      );
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // 분류사례 row expand 시 우측 패널 폭 확장 (340 → 520px)
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  // 검색 교차패널 상태 — AuditSearchContext 공유 (LeftAiPanel 이 사이드바로 이전돼도 연동)
  const {
    searchContext,
    setSearchContext,
    searchResults,
    setSearchResults,
    subdivisionPreview,
    setSubdivisionPreview,
    focusedCase,
    setFocusedCase,
    googleImageQ,
    setGoogleImageQ,
    searchSubmission,
  } = useAuditSearch();
  // 모바일/좁은 화면 — 좌·우 패널을 grid 컬럼(빈 32px 칸) 대신 오버레이 드로어로 전환.
  // 가운데(호·소호·세번 표)를 전체폭으로 → 세번분류 모바일 가독성 확보.
  const isMobile = viewportW < 860;

  // loginHS: 검색은 메인 사이드바로 통합 → 좌측 LeftAiPanel 컬럼 제거(2컬럼).
  // embedded(앱 셸=사이드바 존재) 일 때만 — 비로그인 standalone(?audit=onto-detail, 사이드바 없음)
  // 은 자체 LeftAiPanel 유지(3컬럼)해야 게스트가 검색 가능.
  const searchInSidebar = isLoginHSProduct() && embedded;

  // 모바일 — 단계 이동(부→류→호→세번) 시 새 목록이 보이도록 스크롤 맨 위로.
  //   (세로 스택이라 이전 목록 아래 렌더된 새 내용이 화면 밖에 있으면
  //    "탭했는데 아무 일도 없다"로 느껴짐)
  const rootRef = React.useRef(null);
  const centerScrollRef = React.useRef(null);
  const headingBreadcrumbRef = React.useRef(null);
  const headingTabsBarRef = React.useRef(null);
  const [headingBreadcrumbHeight, setHeadingBreadcrumbHeight] = useState(0);
  const [jumpBackToTop, setJumpBackToTop] = useState(false);
  const headingTabsJumpOffset = headingBreadcrumbHeight + 16;
  // 명시적 검색 제출 즉시 결과 영역을 상단으로 이동한다. URL의 sq 변경을
  // 기다리지 않으므로 느린 조회와 같은 검색어 재검색에서도 일관되게 동작한다.
  useLayoutEffect(() => {
    if (!searchSubmission?.q) return;
    const scroller = centerScrollRef.current;
    if (scroller) scroller.scrollTop = 0;
  }, [searchSubmission]);
  const syncHeadingTabsJumpState = React.useCallback(() => {
    const scroller = centerScrollRef.current;
    const target = headingTabsBarRef.current;
    if (!scroller || (!target && !sq)) {
      setJumpBackToTop(false);
      return;
    }
    // 검색 결과 단계(sq)에는 하단 탭 바가 없다 — 결과 맨 아래가 목표(2026-09-14 사용자 요청: 상세의 ↓/↑ 버튼을 결과 화면에도).
    const targetScrollTop = target
      ? headingTabsScrollTop({
        scrollTop: scroller.scrollTop,
        scrollerTop: scroller.getBoundingClientRect().top,
        targetTop: target.getBoundingClientRect().top,
        offset: headingTabsJumpOffset,
      })
      : Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    setJumpBackToTop(
      shouldJumpBackToTop({
        scrollTop: scroller.scrollTop,
        targetScrollTop,
        clientHeight: scroller.clientHeight,
        scrollHeight: scroller.scrollHeight,
      }),
    );
  }, [headingTabsJumpOffset, sq]);
  const handleHeadingTabsJump = () => {
    const scroller = centerScrollRef.current;
    const target = headingTabsBarRef.current;
    if (!scroller) return;
    if (jumpBackToTop) {
      scroller.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!target) {
      if (sq) scroller.scrollTo({ top: Math.max(0, scroller.scrollHeight - scroller.clientHeight), behavior: "smooth" });
      return;
    }
    const nextTop = headingTabsScrollTop({
      scrollTop: scroller.scrollTop,
      scrollerTop: scroller.getBoundingClientRect().top,
      targetTop: target.getBoundingClientRect().top,
      offset: headingTabsJumpOffset,
    });
    scroller.scrollTo({ top: nextTop, behavior: "smooth" });
  };
  useEffect(() => {
    if (!isMobile) return;
    let n = rootRef.current;
    while (n) {
      if (n.scrollTop > 0) n.scrollTop = 0;
      n = n.parentElement;
    }
    window.scrollTo(0, 0);
  }, [section, chapter, hs, hsk, sq]); // eslint-disable-line react-hooks/exhaustive-deps

  // viewport 가 좁아지면 자동 close (열려 있을 때만 끔)
  useEffect(() => {
    if (viewportW < 1100) {
      setLeftOpen(false);
      setRightOpen(false);
    }
  }, [viewportW]);

  const [lang, setLang] = useState("kor");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // level 자동 감지 — priority: hsk > hs(4) > chapter(2) > section > root
  const level = (() => {
    if (hsk && /^[0-9]{10}$/.test(hsk)) return "hsk";
    if (hs && /^[0-9]{4}$/.test(hs)) return "hierarchy";
    if (chapter && /^[0-9]{1,2}$/.test(chapter)) return "heading";
    if (section && /^[0-9]{1,2}$/.test(section)) return "chapter";
    if (sq) return "search"; // 검색결과 종합 (CLHS 5-섹션)
    return "section";
  })();

  // 브레드크럼과 자료 탭은 같은 중앙 스크롤 영역에서 sticky다. 브레드크럼은
  // 화면 폭에 따라 1~2줄이 되므로 데이터가 렌더된 뒤 실제 높이를 측정한다.
  React.useLayoutEffect(() => {
    const breadcrumb = headingBreadcrumbRef.current;
    if (!breadcrumb) {
      setHeadingBreadcrumbHeight(0);
      return undefined;
    }
    const updateHeight = () => {
      const nextHeight = Math.ceil(breadcrumb.getBoundingClientRect().height);
      setHeadingBreadcrumbHeight((current) =>
        current === nextHeight ? current : nextHeight,
      );
    };
    updateHeight();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }
    const observer = new ResizeObserver(updateHeight);
    observer.observe(breadcrumb);
    return () => observer.disconnect();
  }, [level, data, viewportW]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(syncHeadingTabsJumpState);
    return () => window.cancelAnimationFrame(frame);
  }, [section, chapter, hs, hsk, level, data, syncHeadingTabsJumpState]);

  // 부/류/호 브라우징(section/chapter/heading)은 이제 속견표 그리드로 통일 —
  //   구 onto-detail 계층 화면(부→류 표)은 미노출(사용자 요청 2026-07-14).
  //   검색(sq)·세번상세(hierarchy/hsk)·hsOverride(셸 임베드)는 그대로 유지.
  //   ⚠ activeAudit==="onto-detail" 게이트 필수 — quickref 로 바꾼 뒤에도 이 컴포넌트가
  //     keep-alive 로 마운트돼 있어(레벨=section) 게이트 없으면 무한 리다이렉트 루프.
  const activeAudit =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("audit")
      : null;
  const redirectBrowseToQuickref =
    isLoginHSProduct() &&
    embedded &&
    !hsOverride &&
    !sq &&
    activeAudit === "onto-detail" &&
    (level === "section" || level === "chapter" || level === "heading");
  useEffect(() => {
    if (!redirectBrowseToQuickref) return;
    const u = new URL(window.location.href);
    u.searchParams.set("audit", "quickref");
    u.searchParams.set("noextra", "1"); // 관세율표 모드(그리드 + 좌측 접힘)
    if (level === "heading" && chapter) {
      u.searchParams.set("qch", String(chapter).padStart(2, "0")); // 류→호 속견표
    } else {
      u.searchParams.delete("qch");
    }
    ["section", "chapter", "hs", "hsk", "qhs", "sq"].forEach((k) =>
      u.searchParams.delete(k),
    );
    window.history.replaceState({}, "", u.toString());
    window.dispatchEvent(new PopStateEvent("popstate"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectBrowseToQuickref, level, chapter]);

  useEffect(() => {
    // section / chapter / heading level 은 정적 데이터 — backend fetch 불필요
    if (level !== "hierarchy" && level !== "hsk") {
      setData(null);
      setLoading(false);
      return;
    }
    // hs4 도출 — hsk(10자리)가 있으면 그게 가장 구체적이므로 hsk 우선.
    //   ⚠ 간헐버그: 이전 화면의 stale hs(예 4202)가 URL 에 남고 새 hsk(8529101000)로 navigate
    //   시, hs 우선이면 그리드가 4202(stale)·우측 패널만 8529 로 불일치. hsk 우선으로 해소.
    const hs4 =
      hsk && /^[0-9]{10}$/.test(hsk)
        ? hsk.slice(0, 4)
        : hs && /^[0-9]{4}$/.test(hs)
          ? hs
          : null;
    if (!hs4) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    fetch(`/api/onto/heading-detail?hs=${hs4}&lang=${lang}`)
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then(setData)
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, [level, hs, hsk, lang]);

  // 브라우징 레벨은 위 useEffect 가 quickref 로 리다이렉트 — 구 화면 렌더 생략(깜빡임 방지).
  if (redirectBrowseToQuickref) return null;

  return (
    <>
      <style>{`
        @media print {
          .audit-print-hide { display: none !important; }
          .audit-root { background: white !important; }
          table { page-break-inside: avoid; }
          tr { page-break-inside: avoid; }
          body { background: white !important; }
        }
      `}</style>
      <div
        style={{
          minHeight: embedded ? "100%" : "100vh",
          height: embedded ? "100%" : undefined,
          display: "flex",
          background: "var(--audit-bg)",
          color: "var(--audit-text)",
        }}
      >
        {/* embedded(메인 레이아웃 통합) 모드에선 메인 Sidebar 가 좌측을 담당 —
            AuditSideNav 중복 표시 안 함. 모바일은 standalone 도 숨김 (세로 레일이
            본문을 잠식 + 브레드크럼이 계층 이동을 담당) */}
        {!embedded && !isMobile && (
          <div className="audit-print-hide">
            <AuditSideNav />
          </div>
        )}
        <div
          ref={rootRef}
          className="audit-root"
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            height: embedded ? "100%" : "100vh",
            overflow: "hidden",
          }}
        >
          {/* 상단 bar — 모바일 숨김. 검색 단계(level=search)에선 검색바=전역헤더,
              영문/인쇄=탭줄로 이동했으므로 이 중복 줄도 숨겨 상단 공백 제거. */}
          {!isMobile && level !== "search" && (
            <div
              className="audit-print-hide"
              style={{
                padding: "10px 16px",
                borderBottom: "1px solid var(--audit-border)",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexShrink: 0,
              }}
            >
              {fromChapter && (
                <button
                  onClick={() => {
                    window.history.pushState({}, "", "/?audit=quickref");
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  style={backBtnStyle}
                  title={`속견표(제${parseInt(fromChapter, 10)}류)로 돌아가기`}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "var(--audit-accent-dark)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "var(--audit-accent)";
                  }}
                >
                  <ArrowLeft size={13} /> 속견표(제{parseInt(fromChapter, 10)}
                  류)
                </button>
              )}
              {data && (
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--audit-text-muted)",
                    marginLeft: 8,
                  }}
                >
                  제{data.chapter ? String(parseInt(data.chapter, 10)) : "?"}류
                  · 제{data.hs4}호
                </span>
              )}
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                {/* 내 품목 별 — 셸(hsOverride)에선 QuickRefPage 가 제공하므로 standalone 만 */}
                {!hsOverride && hs && /^[0-9]{4}$/.test(hs) && (
                  <WatchStar
                    code={hs}
                    name={
                      data && data.heading_popum_ko
                        ? String(data.heading_popum_ko).split("\n")[0]
                        : `제${hs}호`
                    }
                  />
                )}
                <button
                  onClick={() => setLang(lang === "kor" ? "eng" : "kor")}
                  style={topBtnStyle}
                  title="한↔영 전환"
                >
                  <Languages size={12} /> {lang === "kor" ? "영문" : "한글"}
                </button>
                <button
                  onClick={() => window.print()}
                  style={topBtnStyle}
                  title="인쇄 미리보기"
                >
                  <Printer size={12} /> 인쇄
                </button>
              </div>
            </div>
          )}

          {/* 본문 — 3-panel grid. 각 panel 독립 스크롤 (좌 검색창 고정, 가운데·우측 자체 scroll) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: detailGridColumns({
                isMobile,
                searchInSidebar,
                leftOpen,
                leftWidth,
                rightOpen,
                evidenceExpanded,
              }),
              gap: 12,
              padding:
                level === "search"
                  ? isMobile
                    ? "4px 10px 8px"
                    : "4px 16px 12px"
                  : isMobile
                    ? "8px 10px"
                    : "12px 16px",
              maxWidth: 1920,
              width: "100%",
              margin: "0 auto",
              position: "relative",
              transition: "grid-template-columns 0.12s",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            {/* 좌측 패널 너비 드래그 핸들 (좌/중앙 사이 세로선) — 데스크톱만 */}
            {!searchInSidebar && !isMobile && leftOpen && (
              <div
                onMouseDown={handleLeftResizeStart}
                title="드래그하여 좌측 패널 너비 조절 (더블클릭: 초기화)"
                onDoubleClick={() => setLeftWidth(320)}
                style={{
                  position: "absolute",
                  top: 12,
                  bottom: 12,
                  left: 16 + leftWidth,
                  width: 12,
                  cursor: "col-resize",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: 3,
                    height: 44,
                    borderRadius: 2,
                    background: "var(--audit-border-strong, #dadce0)",
                  }}
                />
              </div>
            )}
            {/* === 좌: AI panel (검색창 고정, 짧은 콘텐츠) === */}
            {!searchInSidebar && (
              <div
                style={
                  isMobile
                    ? leftOpen
                      ? {
                          position: "fixed",
                          top: 0,
                          left: 0,
                          bottom: 0,
                          width: "min(88vw, 380px)",
                          zIndex: 60,
                          background: "var(--audit-bg)",
                          boxShadow: "2px 0 18px rgba(0,0,0,0.22)",
                          overflowY: "auto",
                          overflowX: "hidden",
                          padding: 8,
                          boxSizing: "border-box",
                        }
                      : { display: "none" }
                    : {
                        minWidth: 0,
                        height: "100%",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }
                }
              >
                {leftOpen ? (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <button
                      onClick={() => setLeftOpen(false)}
                      style={collapseBtnStyle}
                      title="좌 패널 접기"
                    >
                      <ChevLeft size={14} /> AI 검색
                    </button>
                    <LeftAiPanel
                      currentChapter={chapter}
                      onAnyAction={() => setRightOpen(true)}
                      onSearchContextChange={setSearchContext}
                      onFocusCase={(c) => {
                        setFocusedCase(c);
                        setRightOpen(true);
                      }}
                      onGoogleImageOpen={setGoogleImageQ}
                      onSearchResults={setSearchResults}
                      onSubdivisionPreview={setSubdivisionPreview}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setLeftOpen(true)}
                    style={{ ...collapseBtnStyle, justifyContent: "center" }}
                    title="AI 검색 펼치기"
                  >
                    <ChevRight size={14} />
                  </button>
                )}
              </div>
            )}

            {/* === 가운데: 2-column drill (patron + detail). 자체 scroll === */}
            <div
              ref={centerScrollRef}
              data-testid="heading-center-scroll"
              onScroll={syncHeadingTabsJumpState}
              style={{
                minWidth: 0,
                height: "100%",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {/* 검색 → 호 상세 진입 후 종합 뷰 복귀 링크 (sq 유지 상태) */}
              {sq && (level === "hierarchy" || level === "hsk") && (
                <button
                  onClick={() => {
                    const u = new URL(window.location.href);
                    ["section", "chapter", "hs", "hsk"].forEach((k) =>
                      u.searchParams.delete(k),
                    );
                    window.history.pushState({}, "", u.toString());
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    border: "1px solid var(--audit-border)",
                    background: "var(--audit-bg-subtle)",
                    color: "var(--audit-accent)",
                    borderRadius: 4,
                    padding: "3px 10px",
                    fontSize: 12,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    marginBottom: 6,
                  }}
                  title={`"${sq}" 검색결과 종합으로 돌아가기`}
                >
                  ‹ 검색결과 종합 ({sq})
                </button>
              )}

              {/* breadcrumb */}
              {(level === "hierarchy" || level === "hsk") && data && (
                <HeadingBreadcrumb
                  stickyRef={headingBreadcrumbRef}
                  section={data.section}
                  chapter={data.chapter}
                  hs4={data.hs4}
                  heading_popum_ko={data.heading_popum_ko}
                />
              )}
              {(level === "chapter" || level === "heading") && (
                <HeadingBreadcrumb
                  stickyRef={headingBreadcrumbRef}
                  section={
                    level === "chapter"
                      ? Number(section)
                      : level === "heading"
                        ? sectionIdOfChapter(chapter)
                        : null
                  }
                  chapter={
                    level === "heading"
                      ? String(chapter).padStart(2, "0")
                      : null
                  }
                  hs4={null}
                  heading_popum_ko={null}
                />
              )}

              {/* hierarchy/hsk level 의 fetch loading/error 처리 */}
              {(level === "hierarchy" || level === "hsk") &&
                hs &&
                !/^[0-9]{4}$/.test(hs) && (
                  <div style={emptyStyle}>
                    유효하지 않은 HS 코드: <code>{hs}</code> — 4자리 숫자가
                    필요합니다.
                  </div>
                )}
              {(level === "hierarchy" || level === "hsk") && loading && (
                <div
                  style={{
                    color: "var(--audit-text-muted)",
                    fontSize: 13,
                    marginTop: 8,
                  }}
                >
                  불러오는 중…
                </div>
              )}
              {(level === "hierarchy" || level === "hsk") && err && (
                <div
                  style={{
                    color: "#d93025",
                    fontSize: 12,
                    padding: 10,
                    border: "1px solid #d93025",
                    borderRadius: 4,
                    marginTop: 8,
                  }}
                >
                  오류: {err}
                </div>
              )}

              {/* 검색결과 종합 (sq 단독) — CLHS 5-섹션 */}
              {level === "search" && (
                <SearchResultTabs
                  q={sq}
                  tariffData={
                    searchResults && searchResults.q === sq
                      ? searchResults
                      : null
                  }
                  subdivisionPreview={subdivisionPreview}
                  activeTab={params.tab || "all"}
                  lang={lang}
                  onToggleLang={() => setLang(lang === "kor" ? "eng" : "kor")}
                  onPrint={() => window.print()}
                  scrollContainerRef={centerScrollRef}
                  onTab={(tabId) => {
                    const u = new URL(window.location.href);
                    if (tabId && tabId !== "all") u.searchParams.set("tab", tabId);
                    else u.searchParams.delete("tab");
                    window.history.pushState({}, "", u.toString());
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  onRowClick={(hs4, hskCode) => {
                    const u = new URL(window.location.href);
                    ["section", "chapter"].forEach((k) =>
                      u.searchParams.delete(k),
                    );
                    u.searchParams.set("hs", hs4);
                    if (hskCode) u.searchParams.set("hsk", hskCode);
                    else u.searchParams.delete("hsk");
                    window.history.pushState({}, "", u.toString());
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                />
              )}

              {/* 2-column drill view */}
              {level !== "search" && (
                <div style={{ marginTop: 10 }}>
                  <HierarchyDrillView
                    level={level}
                    section={section}
                    chapter={chapter}
                    hs={hs}
                    hsk={hsk}
                    sub={sub}
                    data={data}
                    lang={lang}
                    isMobile={isMobile}
                    searchContext={searchContext}
                    onHsSelect={onHsSelect}
                    onRowClick={(hskClicked) => {
                      const u = new URL(window.location.href);
                      u.searchParams.set("hsk", hskClicked);
                      u.searchParams.delete("sub"); // 세번 클릭 시 소호 필터 해제
                      window.history.pushState({}, "", u.toString());
                      window.dispatchEvent(new PopStateEvent("popstate"));
                    }}
                  />
                </div>
              )}
              {/* 하단 탭 — 호(hierarchy)/세번(hsk) 단계 모두 통칙을 맨 앞에 둔다.
                  세번 단계는 hsk 전달 시
                  분류사례가 세번 정확 매칭. */}
              {(level === "hierarchy" || level === "hsk") && data && (
                <div data-testid="heading-tabs-region">
                  <HeadingTabs
                    hs4={data.hs4}
                    chapter={data.chapter}
                    section={data.section}
                    lang={lang}
                    hsk={level === "hsk" ? hsk : null}
                    tabBarRef={headingTabsBarRef}
                    stickyTop={headingBreadcrumbHeight}
                  />
                </div>
              )}

              {/* 가운데 하단 플로팅 검색바 — standalone(헤더 없음) 브라우징 시에만.
                  앱 셸(searchInSidebar)은 상단 헤더 검색이 상시 담당 → 플로팅 숨김(1개 불변식). */}
              {shouldShowFloatingSearch({
                isMobile,
                hasQuery: !!sq,
                hasHeaderSearch: searchInSidebar,
              }) && <CenterSearchBar />}
            </div>

            {/* === 우: 근거 자료 (EvidencePanel). 자체 scroll === */}
            <div
              style={
                isMobile
                  ? rightOpen
                    ? {
                        position: "fixed",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: "min(92vw, 440px)",
                        zIndex: 60,
                        background: "var(--audit-bg)",
                        boxShadow: "-2px 0 18px rgba(0,0,0,0.22)",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: 8,
                        boxSizing: "border-box",
                      }
                    : { display: "none" }
                  : rightOpen
                    ? {
                        minWidth: 0,
                        height: "100%",
                        overflowY: "auto",
                        overflowX: "hidden",
                      }
                    : {
                        position: "absolute",
                        top: 12,
                        right: 4,
                        width: 24,
                        height: 30,
                        zIndex: 20,
                        overflow: "visible",
                      }
              }
            >
              {rightOpen ? (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  <button
                    onClick={() => setRightOpen(false)}
                    style={collapseBtnStyle}
                    title="우 패널 접기"
                  >
                    <ChevRight size={14} /> 근거 자료
                  </button>
                  <EvidencePanel
                    hs={hs}
                    hsk={hsk}
                    chapter={chapter}
                    heading_popum_ko={data?.heading_popum_ko}
                    lang={lang}
                    onExpandChange={setEvidenceExpanded}
                    searchContext={searchContext}
                    focusedCase={focusedCase}
                    onClearFocusedCase={() => setFocusedCase(null)}
                  />
                </div>
              ) : (
                <button
                  onClick={() => setRightOpen(true)}
                  style={{
                    ...collapseBtnStyle,
                    justifyContent: "center",
                    width: 24,
                    height: 30,
                    padding: 0,
                    background: "var(--audit-bg-elevated)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  }}
                  title="근거 자료 펼치기"
                  aria-label="근거 자료 펼치기"
                >
                  <ChevLeft size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {(((level === "hierarchy" || level === "hsk") && data) || (level === "search" && sq)) && (
        <button
          type="button"
          data-testid="scroll-jump"
          onClick={handleHeadingTabsJump}
          className="audit-print-hide"
          style={headingTabsJumpStyle}
          title={
            jumpBackToTop
              ? "페이지 상단으로 이동"
              : level === "search"
                ? "결과 맨 아래로 이동"
                : "통칙·부주·류주·호해설·분류사례·이미지 탭으로 이동"
          }
          aria-label={jumpBackToTop ? "페이지 상단으로 이동" : level === "search" ? "결과 맨 아래로 이동" : "하단 자료 탭으로 이동"}
        >
          {jumpBackToTop ? <ArrowUp size={17} /> : <ArrowDown size={17} />}
        </button>
      )}
      {/* 모바일: 패널 오버레이 백드롭 + 가장자리 열기 탭 */}
      {isMobile && (leftOpen || rightOpen) && (
        <div
          onClick={() => {
            setLeftOpen(false);
            setRightOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 55,
          }}
        />
      )}
      {isMobile && !leftOpen && (
        <button
          onClick={() => {
            setLeftOpen(true);
            setRightOpen(false);
          }}
          style={mobileTabLeft}
          title="AI 검색 열기"
        >
          AI 검색
        </button>
      )}
      {isMobile && !rightOpen && (
        <button
          onClick={() => {
            setRightOpen(true);
            setLeftOpen(false);
          }}
          style={mobileTabRight}
          title="근거 자료 열기"
        >
          근거 자료
        </button>
      )}
      <GoogleImageOverlay
        q={googleImageQ}
        onClose={() => setGoogleImageQ(null)}
      />
    </>
  );
}

const topBtnStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "transparent",
  border: "1px solid var(--audit-border)",
  borderRadius: 4,
  padding: "3px 8px",
  cursor: "pointer",
  fontSize: 12,
  color: "var(--audit-text-muted)",
  fontFamily: "inherit",
};

// 속견표 복귀 버튼 — accent 채움으로 존재감 강조 (사용자 요청).
const backBtnStyle = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  background: "var(--audit-accent)",
  border: "1px solid var(--audit-accent)",
  borderRadius: 6,
  padding: "5px 12px",
  cursor: "pointer",
  fontSize: 12.5,
  fontWeight: 600,
  color: "#fff",
  fontFamily: "inherit",
  boxShadow: "0 1px 3px rgba(26,115,232,0.3)",
  transition: "background 0.12s",
};

const emptyStyle = {
  padding: 16,
  border: "1px dashed var(--audit-border-strong)",
  borderRadius: 6,
  background: "var(--audit-bg-subtle)",
  color: "var(--audit-text-muted)",
  fontSize: 13,
};

const collapseBtnStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "var(--audit-bg-subtle)",
  border: "1px solid var(--audit-border)",
  borderRadius: 4,
  padding: "5px 8px",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 12,
  color: "var(--audit-text-muted)",
  width: "100%",
  justifyContent: "flex-start",
};

const headingTabsJumpStyle = {
  position: "fixed",
  left: "50%",
  bottom: 18,
  zIndex: 45,
  width: 32,
  height: 32,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  borderRadius: "50%",
  background: "#202124",
  color: "#fff",
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(0,0,0,0.28)",
  transform: "translateX(-50%)",
};

// 모바일 가장자리 세로 탭 — 패널 오버레이 열기 (가운데 콘텐츠 가리지 않음)
const mobileTabLeft = {
  position: "fixed",
  left: 0,
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 50,
  padding: "12px 5px",
  writingMode: "vertical-rl",
  background: "var(--audit-accent, #1a1b1e)",
  color: "#fff",
  border: "none",
  borderRadius: "0 8px 8px 0",
  fontSize: 12,
  fontFamily: "inherit",
  letterSpacing: "1px",
  cursor: "pointer",
  boxShadow: "2px 0 8px rgba(0,0,0,0.2)",
};
const mobileTabRight = {
  ...mobileTabLeft,
  left: "auto",
  right: 0,
  borderRadius: "8px 0 0 8px",
  boxShadow: "-2px 0 8px rgba(0,0,0,0.2)",
};
